/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";

// Images
import team2 from "assets/images/team-2.jpg";

// Material-UI components
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import apiClient from "../../../apiClient";
import DataTable from "examples/Tables/DataTable";
import Divider from "@mui/material/Divider";
import MDInput from "components/MDInput";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";

const S3_BASE_URL = "https://myflyhomes.s3.ap-south-1.amazonaws.com/";

const toAbsoluteImageUrl = (value) => {
  const cleanValue = (value || "").toString().trim();
  if (!cleanValue) return "";
  if (cleanValue.startsWith("http://") || cleanValue.startsWith("https://")) return cleanValue;
  return `${S3_BASE_URL}${cleanValue}`;
};

const parseImageValues = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map((item) => toAbsoluteImageUrl(item)).filter((item) => !!item);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => toAbsoluteImageUrl(item))
      .filter((item) => !!item);
  }

  return [toAbsoluteImageUrl(value)].filter((item) => !!item);
};

const mergePreviewFiles = (documents, images) => {
  const merged = [...parseImageValues(documents), ...parseImageValues(images)];
  return [...new Set(merged)];
};

// Define the Job component
const Job = ({ title, description }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography display="block" variant="caption" fontWeight="medium">
      {title}
    </MDTypography>
    <MDTypography variant="caption">{description}</MDTypography>
  </MDBox>
);

const Author = ({ image, name, email }) => (
  <MDBox display="flex" alignItems="center" lineHeight={1}>
    <MDAvatar src={image} name={name} size="md" />
    <MDBox ml={2} lineHeight={1}>
      <MDTypography display="block" variant="button" fontWeight="medium">
        {name}
      </MDTypography>
      <MDTypography variant="caption">{email}</MDTypography>
    </MDBox>
  </MDBox>
);

const DocumentsPreview = ({ documents }) => {
  const docs = parseImageValues(documents);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState("");

  if (docs.length === 0) {
    return (
      <MDTypography variant="caption" color="text">
        N/A
      </MDTypography>
    );
  }

  const isImageUrl = (url) => {
    const value = (url || "").toLowerCase();
    return /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(value) || value.includes("/image_");
  };

  return (
    <MDBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
      <MDBox
        component="button"
        type="button"
        onClick={() => setIsPreviewOpen(true)}
        title="View documents"
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          bgcolor: "info.main",
          border: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          cursor: "pointer",
          p: 0,
        }}
      >
        <VisibilityIcon sx={{ fontSize: "1rem" }} />
      </MDBox>
      <Dialog open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent>
          <MDBox>
            {docs.map((doc, index) => (
              <MDBox key={`${doc}-${index}`} mb={2}>
                <MDTypography variant="caption" fontWeight="medium" mb={0.5} display="block">
                  Document {index + 1}
                </MDTypography>
                {isImageUrl(doc) ? (
                  <MDBox
                    component="img"
                    src={doc}
                    alt={`Document preview ${index + 1}`}
                    onClick={() => setEnlargedImage(doc)}
                    sx={{
                      width: "100%",
                      maxHeight: "60vh",
                      objectFit: "contain",
                      borderRadius: "8px",
                      cursor: "zoom-in",
                    }}
                  />
                ) : (
                  <Button
                    component="a"
                    href={doc}
                    target="_blank"
                    rel="noreferrer"
                    variant="outlined"
                    size="small"
                  >
                    Open Document
                  </Button>
                )}
              </MDBox>
            ))}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsPreviewOpen(false)}
            color="primary"
            variant="contained"
            sx={{ color: "#fff" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(enlargedImage)}
        onClose={() => setEnlargedImage("")}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>Image Enlarge Preview</DialogTitle>
        <DialogContent>
          <MDBox
            component="img"
            src={enlargedImage}
            alt="Enlarged document preview"
            sx={{
              width: "100%",
              maxHeight: "85vh",
              objectFit: "contain",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEnlargedImage("")}
            color="primary"
            variant="contained"
            sx={{ color: "#fff" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
};

const FetchLandlordData = ({ onRefresh, editData }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [statusInput, setStatusInput] = useState("Active");
  const [nameFilter, setNameFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [darkMode, setDarkMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    onConfirm: null,
  });

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/landlord");
      // Sort listings by added date in descending order (most recent first)
      const sortedListings = response.data.sort((a, b) => {
        const dateA = new Date(a.added?.at || a.added_at || a.createdAt || 0);
        const dateB = new Date(b.added?.at || b.added_at || b.createdAt || 0);
        return dateB - dateA;
      });
      setData(sortedListings);
    } catch (error) {
      console.error("Error fetching landlord data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [onRefresh]);

  // Handlers
  const handleActivate = async (id, status) => {
    try {
      setConfirmDialog({
        open: true,
        title:
          "Are you sure you want to " +
          (status === "Active" ? "deactivate" : "activate") +
          " this landlord?",
        onConfirm: async () => {
          try {
            await apiClient.put(`/admin/landlord/${id}`, { status: status });
            await fetchData(); // Refresh data while staying on the current page
            // Keep current page after refresh
          } catch (error) {
            console.error("Error updating landlord status:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error activating landlord:", error);
    }
  };

  const handleDelete = async (id, name) => {
    try {
      setConfirmDialog({
        open: true,
        title: `Are you sure you want to delete the landlord "${name}"?`,
        onConfirm: async () => {
          try {
            await apiClient.delete(`/admin/landlord/${id}`);
            fetchData(); // Refresh data after delete
          } catch (error) {
            console.error("Error deleting landlord:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error deleting landlord:", error);
    }
  };

  const handleEdit = (id) => {
    // Handle edit functionality
    console.log("Edit landlord with ID:", id);
    editData(id);
  };

  const handleNameInputChange = (event) => {
    setName(event.target.value);
  };

  const handlePhoneInputChange = (event) => {
    setPhone(event.target.value);
  };

  const handleCityInputChange = (event) => {
    setCity(event.target.value);
  };

  const handleStatusInputChange = (event) => {
    setStatusInput(event.target.value);
  };

  const handleFilterSubmit = () => {
    setNameFilter(name);
    setPhoneFilter(phone);
    setCityFilter(city);
    setStatusFilter(statusInput);
  };

  const handleClearFilters = () => {
    setNameFilter("");
    setPhoneFilter("");
    setCityFilter("");
    setStatusFilter("");
    setName("");
    setPhone("");
    setCity("");
    setStatusInput("Active");
  };

  // Filter data
  const filteredRows = data.filter((row) => {
    return (
      (nameFilter === "" || row.name.toLowerCase().includes(nameFilter.toLowerCase())) &&
      (phoneFilter === "" || row.phone.toLowerCase().includes(phoneFilter.toLowerCase())) &&
      (cityFilter === "" || row.city.toLowerCase().includes(cityFilter.toLowerCase())) &&
      (statusFilter === "" || row.status.toLowerCase().includes(statusFilter.toLowerCase()))
    );
  });

  const columns = [
    { Header: "Name", accessor: "name", width: "25%", align: "left" },
    { Header: "Mobile", accessor: "phone", align: "center" },
    { Header: "City", accessor: "city", align: "center" },
    { Header: "Address", accessor: "address", align: "center" },
    { Header: "Added Date", accessor: "added_date", align: "center" },
    { Header: "Documents", accessor: "documents", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  const rows = filteredRows.map((data) => ({
    name: (
      <Author
        image={parseImageValues(data.images)[0] || team2}
        name={data.name}
        email={data.email}
      />
    ),
    mobile: (
      <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
        {data.email}
      </MDTypography>
    ),
    city: <Job title={data.city} description={data.state} />,
    address: <Job title={data.address} description={data.district} />,
    documents: <DocumentsPreview documents={mergePreviewFiles(data.documents, data.images)} />,
    added_date: new Date(data.added?.at).toLocaleDateString(),
    status: (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={data.status}
          color={data.status === "Active" ? "success" : "warning"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    phone: (
      <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
        {data.phone}
      </MDTypography>
    ),
    city: (
      <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
        {data.city}
      </MDTypography>
    ),
    address: (
      <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
        {data.address}
      </MDTypography>
    ),
    action: (
      <MDBox display="flex" justifyContent="space-around">
        <IconButton onClick={() => handleEdit(data)} color="secondary">
          <EditIcon />
        </IconButton>
        <IconButton
          onClick={() => handleActivate(data._id, data.status)}
          color={data.status === "Active" ? "warning" : "success"}
        >
          <CheckCircleIcon />
        </IconButton>
        <IconButton onClick={() => handleDelete(data._id, data.name)} color="error">
          <DeleteIcon />
        </IconButton>
      </MDBox>
    ),
  }));
  // No data message
  if (loading) return <div>Loading...</div>;
  if (data.length === 0) return <div>No data found.</div>;

  return (
    <div>
      <MDBox
        display="flex"
        gap="1rem"
        mb="1rem"
        justifyContent="left"
        alignItems="left"
        ml="2%"
        width="90%"
      >
        <MDInput
          fullWidth
          label="Name"
          variant="outlined"
          value={name}
          onChange={handleNameInputChange}
        />
        <MDInput
          fullWidth
          label="Phone"
          variant="outlined"
          value={phone}
          onChange={handlePhoneInputChange}
        />
        <MDInput
          fullWidth
          label="City"
          variant="outlined"
          value={city}
          onChange={handleCityInputChange}
        />
        <Select
          fullWidth
          variant="outlined"
          value={statusInput}
          onChange={handleStatusInputChange}
          displayEmpty
        >
          <MenuItem value="">
            <em>Status</em>
          </MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </Select>
        <Button
          variant="contained"
          color="primary"
          onClick={handleFilterSubmit}
          sx={{ color: "#fff" }}
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleClearFilters}
          sx={{ color: "#fff" }}
        >
          Clear
        </Button>
      </MDBox>
      <Divider />
      <DataTable
        table={{ columns, rows }}
        isSorted={true}
        entriesPerPage={true}
        showTotalEntries={true}
        noEndBorder
      />
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: darkMode ? "#121212" : "#fff",
            color: darkMode ? "#fff" : "#000",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1300,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: darkMode ? "#121212" : "#fff",
            color: darkMode ? "#fff" : "#000",
            borderBottom: `1px solid ${darkMode ? "#333" : "#e0e0e0"}`,
          }}
        >
          {confirmDialog.title}
        </DialogTitle>
        <DialogActions
          sx={{
            backgroundColor: darkMode ? "#121212" : "#fff",
            padding: "16px 24px",
          }}
        >
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="inherit"
            sx={{ color: darkMode ? "#ccc" : "#666" }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (confirmDialog.onConfirm) {
                await confirmDialog.onConfirm();
              }
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
            sx={{ color: "#fff", ml: 1 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
FetchLandlordData.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  editData: PropTypes.func.isRequired,
};

export default FetchLandlordData;
